import { Button, DatePicker, Space, Typography } from "antd";
import dayjs from "dayjs";
import { DESCEND } from "common/constants";
import { LoginAsButton } from "common/LoginAsButton";
import { Link } from "react-router-dom";

const { Text } = Typography;
const { RangePicker } = DatePicker;

export const columnSorterFunc = (a, b, field) => {
    if (!a && b) {
        return -1;
    }
    if (a && !b) {
        return 1;
    }
    if (!a && !b) {
        return 0;
    }
    if (typeof a[field] === "number") {
        return a[field] - b[field];
    }
    return a[field]?.localeCompare(b[field]);
};

export const USERID = {
    dataIndex: "userId",
    title: "ID",
};
export const STATUS_DESCR = {
    dataIndex: "statusDescr",
    title: "Status",
};
export const ROLE = {
    dataIndex: "role",
    title: "Role",
};
export const FULL_NAME = {
    dataIndex: "fullName",
    title: "Full Name",
};
export const BIZ_NAME = {
    dataIndex: "businessName",
    title: "Brand",
};
export const EMAIL = {
    dataIndex: "email",
    title: "Email",
};
export const INSERT_DATE = {
    dataIndex: "insertDate",
    title: "Reg. Date",
};
export const DATABASE_NAME = {
    dataIndex: "databaseName",
    title: "Database",
};

export const columnDef_userId = {
    title: USERID.title,
    dataIndex: USERID.dataIndex,
    key: USERID.dataIndex,
    width: "110px",
    // `brand-id-cell` scopes the hover-to-reveal copy-icon CSS (Brands.scss).
    className: "brand-id-cell",
    defaultSortOrder: DESCEND,
    sorter: (a, b) => columnSorterFunc(a, b, USERID.dataIndex),
    render: (text, record) => {
        const isPlain =
            record[ROLE.dataIndex] === "Team Member" ||
            !record.databaseActive;
        return (
            <Text
                copyable={{ text: String(text) }}
                style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                }}>
                {isPlain ? text : <Link to={`${text}`}>{text}</Link>}
            </Text>
        );
    },
};
export const columnDef_status = {
    title: STATUS_DESCR.title,
    dataIndex: STATUS_DESCR.dataIndex,
    key: STATUS_DESCR.dataIndex,
    width: "130px",
    sorter: (a, b) => columnSorterFunc(a, b, STATUS_DESCR.dataIndex),
    filters: [
        { text: "All", value: "All" },
        { text: "Active", value: "Active" },
        { text: "Paused", value: "Paused" },
        { text: "Closed", value: "Closed" },
    ],
    defaultFilteredValue: ["All"],
    onFilter: (value, record) => {
        if (value === "All") {
            return true;
        }
        return record[STATUS_DESCR.dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toLowerCase());
    },
};
export const columnDef_role = {
    title: ROLE.title,
    dataIndex: ROLE.dataIndex,
    key: ROLE.dataIndex,
    width: "130px",
    sorter: (a, b) => columnSorterFunc(a, b, ROLE.dataIndex),
    filters: [
        { text: "Owner", value: "Owner" },
        { text: "Team Member", value: "Team Member" },
    ],
    defaultFilteredValue: ["Owner"],
    onFilter: (value, record) =>
        record[ROLE.dataIndex]
            ?.toString()
            .toLowerCase()
            .includes(value?.toLowerCase()),
};
export const columnDef_login = {
    title: "",
    key: "login",
    width: "80px",
    render: (_text, record) => (
        <LoginAsButton
            type="link"
            size="small"
            style={{ padding: 0 }}
            userId={record.userId}
        />
    ),
};
export const columnDef_fullName = {
    title: FULL_NAME.title,
    dataIndex: FULL_NAME.dataIndex,
    key: FULL_NAME.dataIndex,
    sorter: (a, b) => columnSorterFunc(a, b, FULL_NAME.dataIndex),
};
export const columnDef_bizName = {
    title: BIZ_NAME.title,
    dataIndex: BIZ_NAME.dataIndex,
    key: BIZ_NAME.dataIndex,
    sorter: (a, b) => columnSorterFunc(a, b, BIZ_NAME.dataIndex),
};
export const columnDef_email = {
    title: EMAIL.title,
    dataIndex: EMAIL.dataIndex,
    key: EMAIL.dataIndex,
    sorter: (a, b) => columnSorterFunc(a, b, EMAIL.dataIndex),
};
export const columnDef_insertDate = {
    title: INSERT_DATE.title,
    dataIndex: INSERT_DATE.dataIndex,
    key: INSERT_DATE.dataIndex,
    sorter: (a, b) => columnSorterFunc(a, b, INSERT_DATE.dataIndex),
    // Matches the Brand Portal's date format ("MMM dd, yyyy"), e.g. Jan 05, 2026.
    render: (text) => (text ? dayjs(text).format("MMM DD, YYYY") : null),
    width: "120px",
    // Start–end date range filter. `selectedKeys` holds a single element:
    // the [start, end] dayjs pair from the RangePicker.
    filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
    }) => (
        <div
            style={{ padding: 8, width: 340 }}
            onKeyDown={(e) => e.stopPropagation()}>
            <RangePicker
                value={selectedKeys[0] || null}
                onChange={(dates) =>
                    setSelectedKeys(dates ? [dates] : [])
                }
                style={{ marginBottom: 8, width: "100%" }}
            />
            <Space>
                <Button type="primary" size="small" onClick={() => confirm()}>
                    Filter
                </Button>
                <Button
                    size="small"
                    onClick={() => {
                        clearFilters?.();
                        confirm();
                    }}>
                    Reset
                </Button>
            </Space>
        </div>
    ),
    onFilter: (value, record) => {
        const recordDate = record[INSERT_DATE.dataIndex];
        if (!recordDate) {
            return false;
        }
        const [start, end] = value || [];
        const d = dayjs(recordDate);
        if (start && d.isBefore(dayjs(start), "day")) {
            return false;
        }
        if (end && d.isAfter(dayjs(end), "day")) {
            return false;
        }
        return true;
    },
};
export const columnDef_databaseName = {
    title: DATABASE_NAME.title,
    dataIndex: DATABASE_NAME.dataIndex,
    key: DATABASE_NAME.dataIndex,
    sorter: (a, b) => columnSorterFunc(a, b, DATABASE_NAME.dataIndex),
    render: (text) => text,
    // Wide enough to fit the header label, sort arrows and filter icon —
    // this is the last column, so any overflow is clipped by the scroll area.
    width: "120px",
    // `filters` is injected at render time from the loaded data (Brands.js),
    // since database names are an open-ended set.
    filterSearch: true,
    onFilter: (value, record) => record[DATABASE_NAME.dataIndex] === value,
};

export const brandsColumns = [
    columnDef_login,
    columnDef_userId,
    columnDef_bizName,
    columnDef_fullName,
    columnDef_email,
    columnDef_role,
    columnDef_status,
    columnDef_insertDate,
    columnDef_databaseName,
];
